import { useState, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { useToast } from '@/components/ui/use-toast';
import {
  Loader2, Search, FileText, Download, AlertTriangle, CheckCircle2,
  Receipt, User, Building2, FileCheck, Eye, XCircle, X, CreditCard,
  Plus, UserCheck, ChevronLeft, ChevronRight, Mail,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

// ── Constants ──────────────────────────────────────────────────────────────────

const USO_CFDI_OPTIONS = [
  { code: 'G01', description: 'Adquisición de mercancías' },
  { code: 'G03', description: 'Gastos en general' },
  { code: 'D01', description: 'Honorarios médicos, dentales y gastos hospitalarios' },
  { code: 'D10', description: 'Pagos por servicios educativos (colegiaturas)' },
  { code: 'S01', description: 'Sin efectos fiscales' },
  { code: 'CP01', description: 'Pagos' },
];

const PUBLICO_GENERAL_PROFILE = {
  id: -1,
  profileName: 'RFC Público en General',
  rfc: 'XAXX010101000',
  legalName: 'PUBLICO EN GENERAL',
  taxRegimeCode: '616',
  zipCode: '00000',
  emailForInvoices: '',
  defaultUsoCfdi: 'S01',
  isDefault: false,
};

const TAX_REGIMES = [
  { code: '601', name: 'General de Ley Personas Morales' },
  { code: '603', name: 'Personas Morales con Fines no Lucrativos' },
  { code: '605', name: 'Sueldos y Salarios e Ingresos Asimilados a Salarios' },
  { code: '606', name: 'Arrendamiento' },
  { code: '607', name: 'Régimen de Enajenación o Adquisición de Bienes' },
  { code: '608', name: 'Demás ingresos' },
  { code: '610', name: 'Residentes en el Extranjero sin Establecimiento Permanente en México' },
  { code: '611', name: 'Ingresos por Dividendos (socios y accionistas)' },
  { code: '612', name: 'Personas Físicas con Actividades Empresariales y Profesionales' },
  { code: '614', name: 'Ingresos por intereses' },
  { code: '615', name: 'Régimen de los ingresos por obtención de premios' },
  { code: '616', name: 'Sin obligaciones fiscales' },
  { code: '620', name: 'Sociedades Cooperativas de Producción que optan por diferir sus ingresos' },
  { code: '621', name: 'Incorporación Fiscal' },
  { code: '622', name: 'Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { code: '623', name: 'Opcional para Grupos de Sociedades' },
  { code: '624', name: 'Coordinados' },
  { code: '625', name: 'Régimen de las Actividades Empresariales con ingresos a través de Plataformas Tecnológicas' },
  { code: '626', name: 'Régimen Simplificado de Confianza' },
];

const EMPTY_RECEPTOR_FORM = { profileName: '', rfc: '', legalName: '', taxRegimeCode: '', zipCode: '', emailForInvoices: '', defaultUsoCfdi: 'D10', isDefault: false };

// ── Types ──────────────────────────────────────────────────────────────────────

interface InvoicingProfile {
  id: number;
  profileName: string;
  rfc: string;
  legalName: string;
  isDefault: boolean;
  hasHubConfig: boolean;
  razonSocialNombre?: string | null;
}

interface TaxProfile {
  id: number;
  alumnoId: number;
  profileName: string;
  rfc: string;
  legalName: string;
  taxRegimeCode: string;
  zipCode: string;
  emailForInvoices: string;
  defaultUsoCfdi: string;
  isDefault: boolean;
}

interface InvoiceablePayment {
  id: number;
  date: string;
  amount: string;
  paymentMethod: string;
  reference?: string;
  invoiceNumber: string;
  invoiceTotal: string;
  invoiceId: number;
  alumnoId: number;
  alumnoNombre: string | null;
  discountTotal: string;
  razonSocialName: string | null;
  serieFolio: string | null;
  folioSeriesId?: number | null;
  folio?: number | null;
  serieName?: string | null;
  invoicingSettingsId?: number | null;
}

interface UninvoicedSale {
  id: number;
  invoiceNumber: string;
  date: string;
  status: string;
  total: string;
  pagado: string;
  alumnoId: number;
  alumnoNombre: string | null;
  discountTotal: string;
  saldoNeto: string;
  razonSocialName: string | null;
  serieFolio: string | null;
  paymentFolios?: string[];
}

interface CfdiInvoice {
  id: number;
  uuid: string | null;
  folioFiscal: string | null;
  cfdiType?: string;
  receptorRfc: string | null;
  receptorName: string | null;
  total: string | null;
  status: string;
  emittedAt: string | null;
  xmlUrl: string | null;
  pdfUrl: string | null;
  hubErrorCode: string | null;
  hubErrorMessage: string | null;
  taxProfileName: string | null;
}

interface StampBalance {
  stampsBalance: number;
  stampsUsed: number;
  isUnlimited: boolean;
  expirationDate: string;
  alertaActiva: boolean;
}

interface FolioSeries {
  id: number;
  serie: string;
  descripcion: string | null;
  currentFolio: number;
  isDefault: boolean;
  isActive: boolean;
}

interface PendingPpdInvoice {
  id: number;
  uuid: string | null;
  folioFiscal: string | null;
  receptorRfc: string | null;
  receptorName: string | null;
  total: string | null;
  saldoInsoluto: string | null;
  invoiceId: number | null;
  invoiceNumber: string | null;
  alumnoId: number | null;
  alumnoNombre: string | null;
  stampedAt: string | null;
}

interface PendingComplementPayment {
  id: number;
  date: string;
  amount: string;
  paymentMethod: string;
  reference: string | null;
  folioSeriesId?: number | null;
  folio?: number | null;
  serieName?: string | null;
  invoicingSettingsId?: number | null;
}

// ── Helper ─────────────────────────────────────────────────────────────────────

function formatAmount(amount: string | number | null): string {
  if (amount === null || amount === undefined) return '$0.00';
  const n = typeof amount === 'string' ? parseFloat(amount) : amount;
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

// Same composition PaymentsList.tsx uses for "Folio" — the identifier the user
// actually recognizes from Pagos Recibidos, distinct from invoiceNumber (the
// internal sale/receipt number) and from the CFDI's own fiscal folio.
function formatPaymentFolio(p: { folioSeriesId?: number | null; invoicingSettingsId?: number | null; serieName?: string | null; folio?: number | null }): string | null {
  if (!p.folioSeriesId) return null;
  return `RS${p.invoicingSettingsId}-${p.serieName}-${String(p.folio).padStart(3, '0')}`;
}

const PAGE_SIZE_OPTIONS = [50, 100, 150];

// Keeps tables of 100+ rows from rendering everything at once and puts "Emitir
// Factura" a short, fixed scroll away instead of at the bottom of an unbounded list.
function PaginationBar({ total, page, totalPages, pageSize, onPageChange, onPageSizeChange }: {
  total: number;
  page: number;
  totalPages: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
}) {
  if (total === 0) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-between gap-3 border-t px-4 py-3 flex-wrap">
      <p className="text-xs text-muted-foreground">Mostrando {from}–{to} de {total}</p>
      <div className="flex items-center gap-2">
        <Select value={String(pageSize)} onValueChange={v => onPageSizeChange(Number(v))}>
          <SelectTrigger className="w-[100px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PAGE_SIZE_OPTIONS.map(s => (
              <SelectItem key={s} value={String(s)}>{s} / pág</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page === 1}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="text-xs text-muted-foreground min-w-[90px] text-center">Página {page} de {totalPages}</span>
        <Button variant="outline" size="sm" onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page === totalPages}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

function formatDate(d: string | null): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function statusBadge(status: string) {
  if (status === 'stamped') return <Badge className="bg-green-100 text-green-800">Timbrada</Badge>;
  if (status === 'cancelled') return <Badge className="bg-red-100 text-red-800">Cancelada</Badge>;
  if (status === 'pending') return <Badge className="bg-yellow-100 text-yellow-800">Pendiente</Badge>;
  return <Badge variant="outline">{status}</Badge>;
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function InvoicingConsole() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // ── "Emitir" tab state ────────────────────────────────────────────────────
  const [emitirMode, setEmitirModeRaw] = useState<'cobradas' | 'sin_cobrar'>('cobradas');
  const [paymentSearch, setPaymentSearch] = useState('');
  // How far back to look for unbilled PUE receipts. Defaults to the system default (30 days)
  // but the user can widen it live instead of being stuck with whatever
  // invoicingSettings.maxDaysToInvoice says for that razón social.
  const [invoiceWindow, setInvoiceWindow] = useState<'30' | '90' | '180' | 'all'>('30');
  const [selectedSettingsId, setSelectedSettingsId] = useState<number | null>(null);
  const [selectedFolioSeriesId, setSelectedFolioSeriesId] = useState<number | null>(null);
  const [selectedTaxProfileId, setSelectedTaxProfileId] = useState<number | null>(null); // -1 = público en general
  const [alumnoIdForProfiles, setAlumnoIdForProfiles] = useState<number | null>(null);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<Set<number>>(new Set());
  const [selectedUninvoicedSaleId, setSelectedUninvoicedSaleId] = useState<number | null>(null);
  const [usoCfdi, setUsoCfdi] = useState('D10');
  const [generateError, setGenerateError] = useState<{ message: string; satCode?: string; satDetail?: string } | null>(null);

  // Pagination for the (potentially 100+ row) Cuentas Cobradas / Sin Cobrar tables —
  // keeps the DOM small and puts "Emitir Factura" a short, predictable scroll away
  // instead of at the bottom of an unbounded list. Independent per tab so switching
  // doesn't reset the other's page.
  const [paymentPage, setPaymentPage] = useState(1);
  const [paymentPageSize, setPaymentPageSize] = useState(50);
  const [salePage, setSalePage] = useState(1);
  const [salePageSize, setSalePageSize] = useState(50);

  const setEmitirMode = useCallback((mode: 'cobradas' | 'sin_cobrar') => {
    setEmitirModeRaw(mode);
    setSelectedPaymentIds(new Set());
    setSelectedUninvoicedSaleId(null);
    setSelectedTaxProfileId(null);
    setAlumnoIdForProfiles(null);
    setGenerateError(null);
  }, []);

  // ── "Historial" tab state ─────────────────────────────────────────────────
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRfc, setFilterRfc] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [selectedInvoice, setSelectedInvoice] = useState<CfdiInvoice | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // ── Cancel CFDI state ─────────────────────────────────────────────────────
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelMotivo, setCancelMotivo] = useState<'01' | '02' | '03' | '04'>('02');
  const [cancelUuidRelacionado, setCancelUuidRelacionado] = useState('');

  // ── "Complementos de Pago" tab state ──────────────────────────────────────
  const [ppdSearch, setPpdSearch] = useState('');
  const [selectedPpdId, setSelectedPpdId] = useState<number | null>(null);
  const [selectedComplementPaymentIds, setSelectedComplementPaymentIds] = useState<Set<number>>(new Set());
  const [complementTaxProfileId, setComplementTaxProfileId] = useState<number | null>(null);
  const [complementError, setComplementError] = useState<{ message: string; satCode?: string; satDetail?: string } | null>(null);

  // ── New receptor form state ────────────────────────────────────────────────
  const [showNewReceptorForm, setShowNewReceptorForm] = useState(false);
  const [savingReceptor, setSavingReceptor] = useState(false);
  const [newReceptorForm, setNewReceptorForm] = useState(EMPTY_RECEPTOR_FORM);

  const handleSaveNewReceptor = async () => {
    if (!alumnoIdForProfiles) return;
    const { profileName, rfc, legalName, taxRegimeCode, zipCode, emailForInvoices } = newReceptorForm;
    if (!profileName || !rfc || !legalName || !taxRegimeCode || !zipCode || !emailForInvoices) {
      toast({ title: 'Campos requeridos', description: 'Completa todos los campos obligatorios del receptor', variant: 'destructive' });
      return;
    }
    setSavingReceptor(true);
    try {
      const r = await fetch('/api/cfdi/tax-profiles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...newReceptorForm, alumnoId: alumnoIdForProfiles }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || 'Error al guardar receptor');
      }
      const created: TaxProfile = await r.json();
      await queryClient.invalidateQueries({ queryKey: ['cfdi-tax-profiles', alumnoIdForProfiles] });
      setSelectedTaxProfileId(created.id);
      setUsoCfdi(created.defaultUsoCfdi || 'D10');
      setShowNewReceptorForm(false);
      setNewReceptorForm(EMPTY_RECEPTOR_FORM);
      toast({ title: 'Receptor guardado', description: `${created.profileName} (${created.rfc}) registrado` });
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSavingReceptor(false);
    }
  };

  // ── Queries ───────────────────────────────────────────────────────────────

  const { data: stampBalance } = useQuery<StampBalance>({
    queryKey: ['cfdi-stamp-balance', selectedSettingsId],
    queryFn: () => fetch(
      `/api/cfdi/stamp-balance${selectedSettingsId ? `?settingsId=${selectedSettingsId}` : ''}`,
      { credentials: 'include' }
    ).then(r => r.json()),
    refetchInterval: 60_000,
  });

  const { data: profiles = [] } = useQuery<InvoicingProfile[]>({
    queryKey: ['cfdi-settings-all'],
    queryFn: () => fetch('/api/cfdi/settings/all').then(r => r.json()),
  });

  const { data: allPayments = [], isLoading: paymentsLoading } = useQuery<InvoiceablePayment[]>({
    queryKey: ['cfdi-invoiceable-payments', invoiceWindow],
    queryFn: () => fetch(`/api/cfdi/invoiceable-payments?days=${invoiceWindow}`).then(r => r.json()),
  });

  const { data: uninvoicedSales = [], isLoading: uninvoicedSalesLoading } = useQuery<UninvoicedSale[]>({
    queryKey: ['cfdi-uninvoiced-sales'],
    queryFn: () => fetch('/api/cfdi/uninvoiced-sales').then(r => r.json()),
  });

  const { data: taxProfilesForAlumno = [] } = useQuery<TaxProfile[]>({
    queryKey: ['cfdi-tax-profiles', alumnoIdForProfiles],
    queryFn: () => fetch(`/api/cfdi/tax-profiles?alumnoId=${alumnoIdForProfiles}`).then(r => r.json()),
    enabled: !!alumnoIdForProfiles,
  });

  const { data: folioSeriesList = [] } = useQuery<FolioSeries[]>({
    queryKey: ['cfdi-folio-series-console', selectedSettingsId],
    queryFn: () =>
      fetch(`/api/cfdi/settings/${selectedSettingsId}/series`).then(r => r.json()),
    enabled: !!selectedSettingsId,
  });

  // Auto-select default series when settings change or series load
  useEffect(() => {
    if (folioSeriesList.length > 0) {
      const def = folioSeriesList.find(s => s.isDefault) || folioSeriesList[0];
      setSelectedFolioSeriesId(def.id);
    } else {
      setSelectedFolioSeriesId(null);
    }
  }, [folioSeriesList, selectedSettingsId]);

  // Auto-select first configured emisor so stamp balance shows immediately
  useEffect(() => {
    if (selectedSettingsId === null && profiles.length > 0) {
      const first = profiles.find(p => p.hasHubConfig);
      if (first) setSelectedSettingsId(first.id);
    }
  }, [profiles, selectedSettingsId]);

  const historialParams = new URLSearchParams();
  if (filterStatus && filterStatus !== 'all') historialParams.set('status', filterStatus);
  if (filterRfc) historialParams.set('rfcReceptor', filterRfc);
  if (filterFrom) historialParams.set('from', filterFrom);
  if (filterTo) historialParams.set('to', filterTo);

  const { data: invoices = [], isLoading: invoicesLoading } = useQuery<CfdiInvoice[]>({
    queryKey: ['cfdi-invoices', filterStatus === 'all' ? '' : filterStatus, filterRfc, filterFrom, filterTo],
    queryFn: () => fetch(`/api/cfdi/invoices?${historialParams}`).then(r => r.json()),
  });

  const { data: recentComplements = [], isLoading: complementsLoading } = useQuery<CfdiInvoice[]>({
    queryKey: ['cfdi-invoices', 'complementos-recientes'],
    queryFn: () => fetch('/api/cfdi/invoices?cfdiType=P&status=stamped&limit=15').then(r => r.json()),
  });

  const sendEmailMutation = useMutation({
    mutationFn: async (invoice: CfdiInvoice) => {
      const r = await fetch(`/api/cfdi/invoices/${invoice.id}/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      const json = await r.json().catch(() => ({}));
      if (!r.ok || !json.success) throw new Error(json.message || json.error || 'No se pudo enviar el correo');
      return json as { success: boolean; message: string; to: string };
    },
    onSuccess: (r) => toast({ title: 'Correo enviado', description: r.message }),
    onError: (e: Error) => toast({ title: 'No se pudo enviar el correo', description: e.message, variant: 'destructive' }),
  });

  const { data: pendingPpdInvoices = [], isLoading: ppdLoading } = useQuery<PendingPpdInvoice[]>({
    queryKey: ['cfdi-ppd-pending'],
    queryFn: () => fetch('/api/cfdi/ppd/pending').then(r => r.json()),
  });

  const selectedPpd = pendingPpdInvoices.find(p => p.id === selectedPpdId) || null;

  const { data: pendingComplementPayments = [], isLoading: pendingPaymentsLoading } = useQuery<PendingComplementPayment[]>({
    queryKey: ['cfdi-ppd-pending-payments', selectedPpdId],
    queryFn: () => fetch(`/api/cfdi/ppd/${selectedPpdId}/pending-payments`).then(r => r.json()),
    enabled: !!selectedPpdId,
  });

  const { data: complementTaxProfiles = [] } = useQuery<TaxProfile[]>({
    queryKey: ['cfdi-tax-profiles', selectedPpd?.alumnoId],
    queryFn: () => fetch(`/api/cfdi/tax-profiles?alumnoId=${selectedPpd?.alumnoId}`).then(r => r.json()),
    enabled: !!selectedPpd?.alumnoId,
  });

  // ── Derived ───────────────────────────────────────────────────────────────

  const filteredPayments = allPayments.filter(p => {
    if (!paymentSearch) return true;
    const q = paymentSearch.toLowerCase();
    return (
      (p.alumnoNombre || '').toLowerCase().includes(q) ||
      p.invoiceNumber.toLowerCase().includes(q) ||
      (p.reference || '').toLowerCase().includes(q) ||
      (formatPaymentFolio(p) || '').toLowerCase().includes(q)
    );
  });

  const filteredUninvoicedSales = uninvoicedSales.filter(s => {
    if (!paymentSearch) return true;
    const q = paymentSearch.toLowerCase();
    return (
      (s.alumnoNombre || '').toLowerCase().includes(q) ||
      s.invoiceNumber.toLowerCase().includes(q) ||
      (s.paymentFolios || []).some(f => f.toLowerCase().includes(q))
    );
  });

  const paymentTotalPages = Math.max(1, Math.ceil(filteredPayments.length / paymentPageSize));
  const paymentPageClamped = Math.min(paymentPage, paymentTotalPages);
  const pagedPayments = filteredPayments.slice((paymentPageClamped - 1) * paymentPageSize, paymentPageClamped * paymentPageSize);

  const saleTotalPages = Math.max(1, Math.ceil(filteredUninvoicedSales.length / salePageSize));
  const salePageClamped = Math.min(salePage, saleTotalPages);
  const pagedUninvoicedSales = filteredUninvoicedSales.slice((salePageClamped - 1) * salePageSize, salePageClamped * salePageSize);

  // A new search can shrink the result set below the page the user was on.
  useEffect(() => { setPaymentPage(1); setSalePage(1); }, [paymentSearch]);

  const selectedUninvoicedSale = uninvoicedSales.find(s => s.id === selectedUninvoicedSaleId) || null;

  // Determine alumno IDs from the current selection — payments (multi-select) for
  // "cobradas", or the single selected sale for "sin_cobrar" (PPD siempre es 1 venta = 1 CFDI).
  const selectedPayments = allPayments.filter(p => selectedPaymentIds.has(p.id));
  const uniqueAlumnoIds = emitirMode === 'cobradas'
    ? [...new Set(selectedPayments.map(p => p.alumnoId))]
    : (selectedUninvoicedSale ? [selectedUninvoicedSale.alumnoId] : []);

  // When payments from multiple alumnos are selected, disable receptor selector (only público en general)
  const multipleAlumnos = emitirMode === 'cobradas' && uniqueAlumnoIds.length > 1;

  const handleSelectPayment = useCallback((paymentId: number, checked: boolean) => {
    setSelectedPaymentIds(prev => {
      const next = new Set(prev);
      if (checked) {
        next.add(paymentId);
      } else {
        next.delete(paymentId);
      }
      return next;
    });
    // When first payment selected, load tax profiles for that alumno
    if (checked) {
      const p = allPayments.find(x => x.id === paymentId);
      if (p?.alumnoId && !alumnoIdForProfiles) {
        setAlumnoIdForProfiles(p.alumnoId);
      }
    }
  }, [allPayments, alumnoIdForProfiles]);

  // Operates on the CURRENT PAGE only — selections on other pages are preserved either way,
  // so paginating never silently drops (or silently keeps) rows the user can't currently see.
  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedPaymentIds(prev => {
      const next = new Set(prev);
      for (const p of pagedPayments) {
        if (checked) next.add(p.id); else next.delete(p.id);
      }
      return next;
    });
  }, [pagedPayments]);

  const handleSelectUninvoicedSale = useCallback((sale: UninvoicedSale) => {
    setSelectedUninvoicedSaleId(prev => (prev === sale.id ? null : sale.id));
    setSelectedTaxProfileId(null);
    setAlumnoIdForProfiles(sale.alumnoId);
  }, []);

  // ── Generate CFDI mutation ─────────────────────────────────────────────────

  const generateMutation = useMutation({
    mutationFn: async () => {
      const isPublicoGeneral = selectedTaxProfileId === PUBLICO_GENERAL_PROFILE.id;
      const body: Record<string, unknown> = {
        paymentIds: [...selectedPaymentIds],
        usoCfdi,
        ...(selectedSettingsId ? { settingsId: selectedSettingsId } : {}),
        ...(selectedFolioSeriesId ? { folioSeriesId: selectedFolioSeriesId } : {}),
      };
      if (isPublicoGeneral) {
        body.publicoGeneral = true;
      } else if (selectedTaxProfileId) {
        body.taxProfileId = selectedTaxProfileId;
      }
      const r = await fetch('/api/cfdi/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) {
        const e = new Error(json.error || 'Error al timbrar CFDI') as Error & { satCode?: string; satDetail?: string };
        e.satCode = json.satCode;
        e.satDetail = json.satDetail;
        throw e;
      }
      return json;
    },
    onSuccess: (result) => {
      setGenerateError(null);
      toast({
        title: result.idempotente ? 'CFDI ya existía' : 'CFDI timbrado exitosamente',
        description: `UUID: ${result.uuid} — Total: ${formatAmount(result.total)}${result.emailSent ? ` — Enviado a ${result.emailTo}` : ''}`,
      });
      setSelectedPaymentIds(new Set());
      setSelectedTaxProfileId(null);
      queryClient.invalidateQueries({ queryKey: ['cfdi-invoiceable-payments'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-stamp-balance'] });
    },
    onError: (err: Error & { satCode?: string; satDetail?: string }) => {
      setGenerateError({ message: err.message, satCode: err.satCode, satDetail: err.satDetail });
    },
  });

  // ── Generate PPD invoice mutation (Cuentas Sin Cobrar) ────────────────────

  const generatePpdMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUninvoicedSaleId) throw new Error('Selecciona una venta');
      const isPublicoGeneral = selectedTaxProfileId === PUBLICO_GENERAL_PROFILE.id;
      const body: Record<string, unknown> = {
        invoiceId: selectedUninvoicedSaleId,
        usoCfdi,
        ...(selectedSettingsId ? { settingsId: selectedSettingsId } : {}),
        ...(selectedFolioSeriesId ? { folioSeriesId: selectedFolioSeriesId } : {}),
      };
      if (isPublicoGeneral) {
        body.publicoGeneral = true;
      } else if (selectedTaxProfileId) {
        body.taxProfileId = selectedTaxProfileId;
      }
      const r = await fetch('/api/cfdi/ppd/issue', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) {
        const e = new Error(json.error || 'Error al timbrar factura PPD') as Error & { satCode?: string; satDetail?: string };
        e.satCode = json.satCode;
        e.satDetail = json.satDetail;
        throw e;
      }
      return json;
    },
    onSuccess: (result) => {
      setGenerateError(null);
      toast({
        title: result.idempotente ? 'Factura PPD ya existía' : 'Factura PPD timbrada exitosamente',
        description: `UUID: ${result.uuid} — Total: ${formatAmount(result.total)}${result.emailSent ? ` — Enviado a ${result.emailTo}` : ''}`,
      });
      setSelectedUninvoicedSaleId(null);
      setSelectedTaxProfileId(null);
      queryClient.invalidateQueries({ queryKey: ['cfdi-uninvoiced-sales'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-ppd-pending'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-stamp-balance'] });
    },
    onError: (err: Error & { satCode?: string; satDetail?: string }) => {
      setGenerateError({ message: err.message, satCode: err.satCode, satDetail: err.satDetail });
    },
  });

  // ── Cancel CFDI mutation ──────────────────────────────────────────────────

  const cancelMutation = useMutation({
    mutationFn: async ({ id, motivo, uuidRelacionado }: { id: number; motivo: string; uuidRelacionado?: string }) => {
      const body: Record<string, unknown> = { motivo };
      if (uuidRelacionado) body.uuidRelacionado = uuidRelacionado;
      const r = await fetch(`/api/cfdi/invoices/${id}/cancelar`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) throw new Error(json.error || 'Error al cancelar CFDI');
      return json;
    },
    onSuccess: () => {
      toast({ title: 'CFDI cancelado', description: 'El comprobante fue cancelado ante el SAT.' });
      setShowCancelDialog(false);
      setCancelUuidRelacionado('');
      setCancelMotivo('02');
      queryClient.invalidateQueries({ queryKey: ['cfdi-invoices'] });
      setSelectedInvoice(prev => prev ? { ...prev, status: 'cancelled' } : null);
    },
    onError: (err: Error) => {
      toast({ title: 'Error al cancelar', description: err.message, variant: 'destructive' });
    },
  });

  // Reset payment selection + receptor when switching the selected PPD invoice
  useEffect(() => {
    setSelectedComplementPaymentIds(new Set());
    setComplementTaxProfileId(null);
    setComplementError(null);
  }, [selectedPpdId]);

  // ── Generate Complemento de Pago mutation ─────────────────────────────────

  const generateComplementMutation = useMutation({
    mutationFn: async () => {
      const isPublicoGeneral = complementTaxProfileId === PUBLICO_GENERAL_PROFILE.id;
      const body: Record<string, unknown> = {
        paymentIds: [...selectedComplementPaymentIds],
        ...(selectedSettingsId ? { settingsId: selectedSettingsId } : {}),
        ...(selectedFolioSeriesId ? { folioSeriesId: selectedFolioSeriesId } : {}),
      };
      if (isPublicoGeneral) {
        body.publicoGeneral = true;
      } else if (complementTaxProfileId) {
        body.taxProfileId = complementTaxProfileId;
      }
      const r = await fetch('/api/cfdi/payment-complements', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await r.json();
      if (!r.ok) {
        const e = new Error(json.error || 'Error al timbrar Complemento de Pago') as Error & { satCode?: string; satDetail?: string };
        e.satCode = json.satCode;
        e.satDetail = json.satDetail;
        throw e;
      }
      return json;
    },
    onSuccess: (result) => {
      setComplementError(null);
      toast({
        title: result.idempotente ? 'Complemento de Pago ya existía' : 'Complemento de Pago timbrado exitosamente',
        description: `UUID: ${result.uuid} — Total: ${formatAmount(result.total)}${result.emailSent ? ` — Enviado a ${result.emailTo}` : ''}`,
      });
      setSelectedComplementPaymentIds(new Set());
      setSelectedPpdId(null);
      queryClient.invalidateQueries({ queryKey: ['cfdi-ppd-pending'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-ppd-pending-payments'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-invoices'] });
      queryClient.invalidateQueries({ queryKey: ['cfdi-stamp-balance'] });
    },
    onError: (err: Error & { satCode?: string; satDetail?: string }) => {
      setComplementError({ message: err.message, satCode: err.satCode, satDetail: err.satDetail });
    },
  });

  // ── PDF loader ─────────────────────────────────────────────────────────────

  const loadPdf = useCallback(async (invoice: CfdiInvoice) => {
    setPdfBlobUrl(null);
    setPdfLoading(true);
    try {
      const r = await fetch(`/api/cfdi/invoices/${invoice.id}/download/pdf`);
      if (!r.ok) throw new Error('No se pudo cargar el PDF');
      const blob = await r.blob();
      setPdfBlobUrl(URL.createObjectURL(blob));
    } catch {
      toast({ title: 'Error', description: 'No se pudo cargar el PDF', variant: 'destructive' });
    } finally {
      setPdfLoading(false);
    }
  }, [toast]);

  const handleSelectInvoice = useCallback((inv: CfdiInvoice) => {
    setSelectedInvoice(inv);
    // PDF proxy only needs the uuid — pdfUrl is not required
    if (inv.uuid && inv.status === 'stamped') {
      loadPdf(inv);
    } else {
      setPdfBlobUrl(null);
    }
  }, [loadPdf]);

  const handleDownloadXml = useCallback(async (invoice: CfdiInvoice) => {
    const r = await fetch(`/api/cfdi/invoices/${invoice.id}/download/xml`);
    if (!r.ok) { toast({ title: 'Error', description: 'XML no disponible', variant: 'destructive' }); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CFDI_${invoice.folioFiscal || invoice.id}.xml`;
    a.click();
    URL.revokeObjectURL(url);
  }, [toast]);

  const handleDownloadPdf = useCallback(async (invoice: CfdiInvoice) => {
    const r = await fetch(`/api/cfdi/invoices/${invoice.id}/download/pdf`);
    if (!r.ok) { toast({ title: 'Error', description: 'PDF no disponible', variant: 'destructive' }); return; }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `CFDI_${invoice.folioFiscal || invoice.id}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  }, [toast]);

  // ── Computed for "Emitir" tab ─────────────────────────────────────────────

  const canEmitir = selectedPaymentIds.size > 0
    && (selectedTaxProfileId !== null)
    && !generateMutation.isPending;

  const canEmitirPpd = selectedUninvoicedSaleId !== null
    && (selectedTaxProfileId !== null)
    && !generatePpdMutation.isPending;

  const receptorOptions: (TaxProfile & { id: number })[] = [
    PUBLICO_GENERAL_PROFILE as unknown as TaxProfile & { id: number },
    ...taxProfilesForAlumno,
  ];

  // ── Computed for "Complementos de Pago" tab ───────────────────────────────

  const filteredPpdInvoices = pendingPpdInvoices.filter(p => {
    if (!ppdSearch) return true;
    const q = ppdSearch.toLowerCase();
    return (
      (p.alumnoNombre || '').toLowerCase().includes(q) ||
      (p.folioFiscal || '').toLowerCase().includes(q) ||
      (p.invoiceNumber || '').toLowerCase().includes(q)
    );
  });

  const handleSelectComplementPayment = useCallback((paymentId: number, checked: boolean) => {
    setSelectedComplementPaymentIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(paymentId); else next.delete(paymentId);
      return next;
    });
  }, []);

  const totalSelectedComplementAmount = pendingComplementPayments
    .filter(p => selectedComplementPaymentIds.has(p.id))
    .reduce((s, p) => s + parseFloat(p.amount), 0);

  const canGenerateComplement = selectedComplementPaymentIds.size > 0
    && complementTaxProfileId !== null
    && !generateComplementMutation.isPending;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Consola de Facturación</h1>
          <p className="text-muted-foreground text-sm">Emisión y consulta de CFDI 4.0</p>
        </div>
        {stampBalance && (
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="text-sm px-3 py-1">
              <Receipt className="w-4 h-4 mr-1" />
              {stampBalance.isUnlimited ? 'Timbres ilimitados' : `${stampBalance.stampsBalance} timbres disponibles`}
            </Badge>
            {stampBalance.alertaActiva && (
              <Badge className="bg-amber-100 text-amber-800 text-sm px-3 py-1">
                <AlertTriangle className="w-4 h-4 mr-1" />
                Alerta de saldo bajo activa
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="emitir">
        <TabsList>
          <TabsTrigger value="emitir">
            <FileText className="w-4 h-4 mr-2" />
            Emitir Factura
          </TabsTrigger>
          <TabsTrigger value="historial">
            <FileCheck className="w-4 h-4 mr-2" />
            Historial de CFDIs
          </TabsTrigger>
          <TabsTrigger value="complementos">
            <CreditCard className="w-4 h-4 mr-2" />
            Complementos de Pago
          </TabsTrigger>
        </TabsList>

        {/* ── Emitir tab ──────────────────────────────────────────────────── */}
        <TabsContent value="emitir" className="space-y-4 mt-4">

          {/* Emisor + Receptor selectors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Emisor */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Emisor (Razón Social)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select
                  value={selectedSettingsId?.toString() ?? undefined}
                  onValueChange={v => {
                    setSelectedSettingsId(v ? parseInt(v) : null);
                    setSelectedFolioSeriesId(null);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar emisor…" />
                  </SelectTrigger>
                  <SelectContent>
                    {profiles.filter(p => p.hasHubConfig).map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        <span className="font-medium">{p.rfc}</span>
                        {' — '}
                        {p.razonSocialNombre || p.legalName}
                        {p.isDefault && <span className="ml-1 text-xs text-muted-foreground">(predeterminado)</span>}
                      </SelectItem>
                    ))}
                    {profiles.filter(p => p.hasHubConfig).length === 0 && (
                      <SelectItem value="_none" disabled>No hay perfiles configurados</SelectItem>
                    )}
                  </SelectContent>
                </Select>

                {/* Folio series selector — shown only when series are configured */}
                {folioSeriesList.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Serie de folio</Label>
                    <Select
                      value={selectedFolioSeriesId?.toString() ?? undefined}
                      onValueChange={v => setSelectedFolioSeriesId(parseInt(v))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Seleccionar serie…" />
                      </SelectTrigger>
                      <SelectContent>
                        {folioSeriesList.map(s => (
                          <SelectItem key={s.id} value={s.id.toString()}>
                            <span className="font-mono font-bold">{s.serie}</span>
                            {s.descripcion && <span className="text-muted-foreground"> — {s.descripcion}</span>}
                            {s.isDefault && <span className="ml-1 text-xs text-muted-foreground">(principal)</span>}
                            <span className="text-xs text-muted-foreground ml-1">folio #{s.currentFolio}</span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Receptor */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Receptor
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {multipleAlumnos ? (
                  <p className="text-sm text-muted-foreground">
                    Varios alumnos seleccionados — solo se puede facturar a Público en General.
                  </p>
                ) : null}
                <Select
                  value={selectedTaxProfileId?.toString() ?? undefined}
                  onValueChange={v => {
                    const id = parseInt(v);
                    setSelectedTaxProfileId(id);
                    if (id !== PUBLICO_GENERAL_PROFILE.id) {
                      setUsoCfdi(taxProfilesForAlumno.find(p => p.id === id)?.defaultUsoCfdi || 'D10');
                    } else {
                      setUsoCfdi('S01');
                    }
                    setShowNewReceptorForm(false);
                  }}
                  disabled={multipleAlumnos}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={
                      uniqueAlumnoIds.length === 0
                        ? (emitirMode === 'cobradas' ? 'Selecciona pagos primero…' : 'Selecciona una venta primero…')
                        : multipleAlumnos
                        ? 'Solo Público en General'
                        : 'Seleccionar receptor…'
                    } />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={PUBLICO_GENERAL_PROFILE.id.toString()}>
                      RFC Público en General (XAXX010101000)
                    </SelectItem>
                    {taxProfilesForAlumno.map(p => (
                      <SelectItem key={p.id} value={p.id.toString()}>
                        <span className="font-medium">{p.rfc}</span>
                        {' — '}{p.profileName}
                        {p.isDefault && <span className="ml-1 text-xs text-muted-foreground">(predeterminado)</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Botón para agregar nuevo receptor */}
                {alumnoIdForProfiles && !multipleAlumnos && !showNewReceptorForm && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full gap-1.5 text-indigo-600 border-indigo-200 hover:bg-indigo-50 mt-1"
                    onClick={() => { setShowNewReceptorForm(true); setNewReceptorForm(EMPTY_RECEPTOR_FORM); }}
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Agregar datos fiscales del receptor
                  </Button>
                )}

                {/* Formulario nuevo receptor inline */}
                {showNewReceptorForm && (
                  <div className="border border-indigo-200 rounded-lg p-3 bg-indigo-50/40 mt-2 space-y-2">
                    <div className="flex items-center justify-between mb-1">
                      <p className="text-xs font-semibold text-indigo-800">Nuevo Receptor CFDI</p>
                      <button
                        type="button"
                        onClick={() => setShowNewReceptorForm(false)}
                        className="p-1 hover:bg-indigo-100 rounded text-indigo-400"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Nombre del perfil *</Label>
                        <Input
                          placeholder='Ej: "Mamá", "Empresa ABC"'
                          value={newReceptorForm.profileName}
                          onChange={e => setNewReceptorForm(p => ({ ...p, profileName: e.target.value }))}
                          className="text-xs h-8 mt-0.5"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">RFC *</Label>
                        <Input
                          placeholder="XAXX010101000"
                          value={newReceptorForm.rfc}
                          onChange={e => setNewReceptorForm(p => ({ ...p, rfc: e.target.value.toUpperCase() }))}
                          className="text-xs h-8 font-mono mt-0.5"
                          maxLength={13}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-600">CP *</Label>
                        <Input
                          placeholder="12345"
                          value={newReceptorForm.zipCode}
                          onChange={e => setNewReceptorForm(p => ({ ...p, zipCode: e.target.value.replace(/\D/g, '').slice(0, 5) }))}
                          className="text-xs h-8 font-mono mt-0.5"
                          maxLength={5}
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Razón Social *</Label>
                        <Input
                          placeholder="Nombre exacto como en el SAT"
                          value={newReceptorForm.legalName}
                          onChange={e => setNewReceptorForm(p => ({ ...p, legalName: e.target.value }))}
                          className="text-xs h-8 mt-0.5"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Régimen Fiscal *</Label>
                        <Select
                          value={newReceptorForm.taxRegimeCode}
                          onValueChange={v => setNewReceptorForm(p => ({ ...p, taxRegimeCode: v }))}
                        >
                          <SelectTrigger className="text-xs h-8 mt-0.5">
                            <SelectValue placeholder="Seleccionar régimen" />
                          </SelectTrigger>
                          <SelectContent>
                            {TAX_REGIMES.map(r => (
                              <SelectItem key={r.code} value={r.code} className="text-xs">
                                <span className="font-mono text-gray-500 mr-1">{r.code}</span> {r.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Email para facturas *</Label>
                        <Input
                          type="email"
                          placeholder="cliente@correo.com"
                          value={newReceptorForm.emailForInvoices}
                          onChange={e => setNewReceptorForm(p => ({ ...p, emailForInvoices: e.target.value }))}
                          className="text-xs h-8 mt-0.5"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs text-gray-600">Uso CFDI predeterminado</Label>
                        <Select
                          value={newReceptorForm.defaultUsoCfdi}
                          onValueChange={v => setNewReceptorForm(p => ({ ...p, defaultUsoCfdi: v }))}
                        >
                          <SelectTrigger className="text-xs h-8 mt-0.5">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {USO_CFDI_OPTIONS.map(o => (
                              <SelectItem key={o.code} value={o.code} className="text-xs">
                                {o.code} — {o.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="col-span-2 flex items-center gap-2">
                        <Checkbox
                          id="receptorIsDefault"
                          checked={newReceptorForm.isDefault}
                          onCheckedChange={v => setNewReceptorForm(p => ({ ...p, isDefault: !!v }))}
                        />
                        <Label htmlFor="receptorIsDefault" className="text-xs text-gray-600 cursor-pointer font-normal">
                          Marcar como receptor predeterminado
                        </Label>
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-1">
                      <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setShowNewReceptorForm(false)}>
                        Cancelar
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 text-xs bg-indigo-600 hover:bg-indigo-700 gap-1"
                        onClick={handleSaveNewReceptor}
                        disabled={savingReceptor}
                      >
                        {savingReceptor ? <Loader2 className="w-3 h-3 animate-spin" /> : <UserCheck className="w-3 h-3" />}
                        Guardar receptor
                      </Button>
                    </div>
                  </div>
                )}

                {selectedTaxProfileId !== null && !multipleAlumnos && (
                  <div className="mt-2">
                    <Label className="text-xs text-muted-foreground">Uso CFDI</Label>
                    {selectedTaxProfileId === PUBLICO_GENERAL_PROFILE.id ? (
                      <p className="mt-1 text-sm px-3 py-2 rounded-md border bg-muted text-muted-foreground">
                        S01 — Sin efectos fiscales
                        <span className="ml-2 text-xs">(obligatorio para Público en General)</span>
                      </p>
                    ) : (
                      <Select value={usoCfdi} onValueChange={setUsoCfdi}>
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {USO_CFDI_OPTIONS.map(o => (
                            <SelectItem key={o.code} value={o.code}>
                              {o.code} — {o.description}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Summary + Emitir button — right below Emisor/Receptor, above the tabs and
              table, so it's visible the instant something is selected regardless of how
              many rows the (now paginated) table below has or how far down it scrolled. */}
          {emitirMode === 'cobradas' && selectedPaymentIds.size > 0 && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">
                      {selectedPaymentIds.size} pago(s) seleccionado(s)
                    </p>
                    <p className="text-lg font-bold">
                      Total: {formatAmount(
                        allPayments
                          .filter(p => selectedPaymentIds.has(p.id))
                          .reduce((s, p) => s + parseFloat(p.amount), 0)
                      )}
                    </p>
                    {selectedTaxProfileId !== null && (
                      <p className="text-xs text-muted-foreground">
                        Receptor: {
                          selectedTaxProfileId === PUBLICO_GENERAL_PROFILE.id
                            ? 'RFC Público en General (XAXX010101000)'
                            : taxProfilesForAlumno.find(p => p.id === selectedTaxProfileId)?.rfc || '—'
                        }
                        {' · '}Uso CFDI: {usoCfdi}
                        {selectedFolioSeriesId && (() => {
                          const s = folioSeriesList.find(x => x.id === selectedFolioSeriesId);
                          return s ? ` · Serie: ${s.serie} #${s.currentFolio}` : null;
                        })()}
                      </p>
                    )}
                  </div>
                  <Button
                    size="lg"
                    disabled={!canEmitir}
                    onClick={() => { setGenerateError(null); generateMutation.mutate(); }}
                  >
                    {generateMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Timbrando…</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" />Emitir CFDI</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {emitirMode === 'sin_cobrar' && selectedUninvoicedSale && (
            <Card className="border-primary/30 bg-primary/5">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Venta: {selectedUninvoicedSale.invoiceNumber}</p>
                    <p className="text-lg font-bold">Total a facturar: {formatAmount(selectedUninvoicedSale.total)}</p>
                    <p className="text-xs text-muted-foreground">
                      Pagado hasta hoy: {formatAmount(selectedUninvoicedSale.pagado)} · Método de pago: PPD (parcialidades)
                    </p>
                    {selectedTaxProfileId !== null && (
                      <p className="text-xs text-muted-foreground">
                        Receptor: {
                          selectedTaxProfileId === PUBLICO_GENERAL_PROFILE.id
                            ? 'RFC Público en General (XAXX010101000)'
                            : taxProfilesForAlumno.find(p => p.id === selectedTaxProfileId)?.rfc || '—'
                        }
                        {' · '}Uso CFDI: {usoCfdi}
                        {selectedFolioSeriesId && (() => {
                          const s = folioSeriesList.find(x => x.id === selectedFolioSeriesId);
                          return s ? ` · Serie: ${s.serie} #${s.currentFolio}` : null;
                        })()}
                      </p>
                    )}
                  </div>
                  <Button
                    size="lg"
                    disabled={!canEmitirPpd}
                    onClick={() => { setGenerateError(null); generatePpdMutation.mutate(); }}
                  >
                    {generatePpdMutation.isPending ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Timbrando…</>
                    ) : (
                      <><FileText className="w-4 h-4 mr-2" />Emitir Factura PPD</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Modo: Cobradas (PUE) vs Sin Cobrar (PPD) */}
          <div className="inline-flex rounded-md border bg-muted p-1">
            <button
              onClick={() => setEmitirMode('cobradas')}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${emitirMode === 'cobradas' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Cuentas Cobradas (PUE)
            </button>
            <button
              onClick={() => setEmitirMode('sin_cobrar')}
              className={`px-3 py-1.5 text-sm font-medium rounded-sm transition-colors ${emitirMode === 'sin_cobrar' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
            >
              Cuentas Sin Cobrar (PPD)
            </button>
          </div>

          {/* Payments / Uninvoiced sales table */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">
                  {emitirMode === 'cobradas' ? 'Cuentas de Recibo Cobradas' : 'Cuentas de Recibo Sin Cobrar'}
                  {emitirMode === 'cobradas' && selectedPaymentIds.size > 0 && (
                    <Badge variant="secondary" className="ml-2">{selectedPaymentIds.size} seleccionadas</Badge>
                  )}
                  {emitirMode === 'sin_cobrar' && selectedUninvoicedSaleId !== null && (
                    <Badge variant="secondary" className="ml-2">1 seleccionada</Badge>
                  )}
                </CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  {emitirMode === 'cobradas' && (
                    <Select value={invoiceWindow} onValueChange={(v) => setInvoiceWindow(v as typeof invoiceWindow)}>
                      <SelectTrigger className="w-44" title="Rango de fechas a mostrar">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="30">Últimos 30 días</SelectItem>
                        <SelectItem value="90">Últimos 90 días</SelectItem>
                        <SelectItem value="180">Últimos 180 días</SelectItem>
                        <SelectItem value="all">Todos los recibos</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  <div className="relative w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar alumno, recibo…"
                      value={paymentSearch}
                      onChange={e => setPaymentSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {emitirMode === 'cobradas' ? (
                paymentsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredPayments.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    {paymentSearch ? 'Sin resultados para la búsqueda' : 'No hay cuentas de recibo cobradas pendientes de facturar'}
                  </p>
                ) : (
                  <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10">
                            <Checkbox
                              checked={pagedPayments.length > 0 && pagedPayments.every(p => selectedPaymentIds.has(p.id))}
                              onCheckedChange={handleSelectAll}
                            />
                          </TableHead>
                          <TableHead>Alumno</TableHead>
                          <TableHead>Recibo</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Método</TableHead>
                          <TableHead>Serie</TableHead>
                          <TableHead className="text-right">Descuento</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedPayments.map(p => (
                          <TableRow
                            key={p.id}
                            className={selectedPaymentIds.has(p.id) ? 'bg-primary/5' : undefined}
                            onClick={() => handleSelectPayment(p.id, !selectedPaymentIds.has(p.id))}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedPaymentIds.has(p.id)}
                                onCheckedChange={checked => {
                                  handleSelectPayment(p.id, !!checked);
                                  if (checked && p.alumnoId) setAlumnoIdForProfiles(p.alumnoId);
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{p.alumnoNombre || '—'}</div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm font-medium">{formatPaymentFolio(p) || p.invoiceNumber}</div>
                              <div className="text-xs text-muted-foreground">{p.invoiceNumber}{p.reference ? ` · ${p.reference}` : ''}</div>
                            </TableCell>
                            <TableCell className="text-sm">{formatDate(p.date)}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{p.paymentMethod}</Badge>
                            </TableCell>
                            <TableCell>
                              {p.razonSocialName ? (
                                <div className="text-xs">
                                  <div className="text-muted-foreground">{p.razonSocialName}</div>
                                  {p.serieFolio && <div className="font-mono">{p.serieFolio}</div>}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {parseFloat(p.discountTotal) > 0 ? (
                                <span className="text-xs text-emerald-600 font-medium">
                                  -{formatAmount(p.discountTotal)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">{formatAmount(p.amount)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <PaginationBar
                    total={filteredPayments.length}
                    page={paymentPageClamped}
                    totalPages={paymentTotalPages}
                    pageSize={paymentPageSize}
                    onPageChange={setPaymentPage}
                    onPageSizeChange={size => { setPaymentPageSize(size); setPaymentPage(1); }}
                  />
                  </>
                )
              ) : (
                uninvoicedSalesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredUninvoicedSales.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    {paymentSearch ? 'Sin resultados para la búsqueda' : 'No hay ventas sin facturar'}
                  </p>
                ) : (
                  <>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10" />
                          <TableHead>Alumno</TableHead>
                          <TableHead>Recibo</TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Serie</TableHead>
                          <TableHead className="text-right">Descuento</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead className="text-right">Pagado</TableHead>
                          <TableHead className="text-right">Saldo neto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {pagedUninvoicedSales.map(s => (
                          <TableRow
                            key={s.id}
                            className={selectedUninvoicedSaleId === s.id ? 'bg-primary/5' : undefined}
                            onClick={() => handleSelectUninvoicedSale(s)}
                            style={{ cursor: 'pointer' }}
                          >
                            <TableCell onClick={e => e.stopPropagation()}>
                              <Checkbox
                                checked={selectedUninvoicedSaleId === s.id}
                                onCheckedChange={() => handleSelectUninvoicedSale(s)}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="font-medium text-sm">{s.alumnoNombre || '—'}</div>
                            </TableCell>
                            <TableCell>
                              {s.paymentFolios && s.paymentFolios.length > 0 ? (
                                <>
                                  <div className="text-sm font-medium">{s.paymentFolios.join(', ')}</div>
                                  <div className="text-xs text-muted-foreground">{s.invoiceNumber}</div>
                                </>
                              ) : (
                                <div className="text-sm">{s.invoiceNumber}</div>
                              )}
                            </TableCell>
                            <TableCell className="text-sm">{formatDate(s.date)}</TableCell>
                            <TableCell>
                              {s.razonSocialName ? (
                                <div className="text-xs">
                                  <div className="text-muted-foreground">{s.razonSocialName}</div>
                                  {s.serieFolio && <div className="font-mono">{s.serieFolio}</div>}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {parseFloat(s.discountTotal) > 0 ? (
                                <span className="text-xs text-emerald-600 font-medium">
                                  -{formatAmount(s.discountTotal)}
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-medium">{formatAmount(s.total)}</TableCell>
                            <TableCell className="text-right text-muted-foreground">{formatAmount(s.pagado)}</TableCell>
                            <TableCell className="text-right font-medium">{formatAmount(s.saldoNeto)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  <PaginationBar
                    total={filteredUninvoicedSales.length}
                    page={salePageClamped}
                    totalPages={saleTotalPages}
                    pageSize={salePageSize}
                    onPageChange={setSalePage}
                    onPageSizeChange={size => { setSalePageSize(size); setSalePage(1); }}
                  />
                  </>
                )
              )}
            </CardContent>
          </Card>

          {/* SAT / timbrado error — persistent alert */}
          {generateError && (
            <Alert variant="destructive" className="relative">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle className="flex items-center gap-2">
                {emitirMode === 'cobradas' ? 'Error al timbrar CFDI' : 'Error al timbrar factura PPD'}
                {generateError.satCode && (
                  <code className="text-xs bg-destructive/20 px-1.5 py-0.5 rounded font-mono">
                    {generateError.satCode}
                  </code>
                )}
              </AlertTitle>
              <AlertDescription className="mt-1 pr-8">
                {generateError.message}
                {generateError.satDetail && (
                  <div className="mt-1 text-xs opacity-80">{generateError.satDetail}</div>
                )}
              </AlertDescription>
              <button
                onClick={() => setGenerateError(null)}
                className="absolute top-3 right-3 opacity-70 hover:opacity-100 transition-opacity"
                aria-label="Cerrar"
              >
                <X className="h-4 w-4" />
              </button>
            </Alert>
          )}

        </TabsContent>

        {/* ── Historial tab ────────────────────────────────────────────────── */}
        <TabsContent value="historial" className="space-y-4 mt-4">

          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Estado</Label>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="stamped">Timbrada</SelectItem>
                      <SelectItem value="cancelled">Cancelada</SelectItem>
                      <SelectItem value="pending">Pendiente</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">RFC Receptor</Label>
                  <Input
                    className="mt-1"
                    placeholder="Buscar RFC…"
                    value={filterRfc}
                    onChange={e => setFilterRfc(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={filterFrom}
                    onChange={e => setFilterFrom(e.target.value)}
                  />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input
                    type="date"
                    className="mt-1"
                    value={filterTo}
                    onChange={e => setFilterTo(e.target.value)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Split panel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* List */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  CFDIs emitidos
                  {!invoicesLoading && (
                    <Badge variant="secondary" className="ml-2">{invoices.length}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {invoicesLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : invoices.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">Sin CFDIs en este periodo</p>
                ) : (
                  <div className="divide-y max-h-[520px] overflow-y-auto">
                    {invoices.map(inv => (
                      <div
                        key={inv.id}
                        onClick={() => handleSelectInvoice(inv)}
                        className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${selectedInvoice?.id === inv.id ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-xs font-mono text-muted-foreground truncate">
                              {inv.uuid || 'Sin UUID'}
                            </p>
                            <p className="text-sm font-medium mt-0.5">
                              {inv.folioFiscal || '—'}
                              {' · '}
                              <span className="text-muted-foreground font-normal">{inv.receptorRfc || '—'}</span>
                              {inv.cfdiType === 'P' && (
                                <Badge variant="outline" className="ml-1.5 text-xs">CRP</Badge>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">{formatDate(inv.emittedAt)}</p>
                          </div>
                          <div className="flex flex-col items-end gap-1 shrink-0">
                            {statusBadge(inv.status)}
                            <span className="text-sm font-bold">{formatAmount(inv.total)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Detail panel */}
            <Card>
              {selectedInvoice ? (
                <>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>Detalle CFDI</span>
                      <div className="flex gap-2">
                        {selectedInvoice.status === 'stamped' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(selectedInvoice)}>
                              <Download className="w-4 h-4 mr-1" />
                              PDF
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDownloadXml(selectedInvoice)}>
                              <Download className="w-4 h-4 mr-1" />
                              XML
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendEmailMutation.mutate(selectedInvoice)}
                              disabled={sendEmailMutation.isPending}
                            >
                              {sendEmailMutation.isPending
                                ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                : <Mail className="w-4 h-4 mr-1" />}
                              Enviar
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              onClick={() => {
                                setCancelMotivo('02');
                                setCancelUuidRelacionado('');
                                setShowCancelDialog(true);
                              }}
                            >
                              <XCircle className="w-4 h-4 mr-1" />
                              Cancelar
                            </Button>
                          </>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Info rows */}
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">UUID SAT</p>
                        <p className="font-mono text-xs break-all">{selectedInvoice.uuid || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Folio Fiscal</p>
                        <p className="font-medium">{selectedInvoice.folioFiscal || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">RFC Receptor</p>
                        <p className="font-medium">{selectedInvoice.receptorRfc || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Nombre Fiscal</p>
                        <p className="font-medium truncate">{selectedInvoice.receptorName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Uso CFDI</p>
                        <p>{selectedInvoice.taxProfileName || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Estado</p>
                        {statusBadge(selectedInvoice.status)}
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Fecha emisión</p>
                        <p>{formatDate(selectedInvoice.emittedAt)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total</p>
                        <p className="font-bold text-base">{formatAmount(selectedInvoice.total)}</p>
                      </div>
                    </div>

                    {selectedInvoice.hubErrorMessage && (
                      <div className="rounded-md bg-red-50 border border-red-200 p-3">
                        <p className="text-xs font-medium text-red-700">{selectedInvoice.hubErrorCode}</p>
                        <p className="text-xs text-red-600 mt-0.5">{selectedInvoice.hubErrorMessage}</p>
                      </div>
                    )}

                    {/* PDF viewer */}
                    {selectedInvoice.status === 'stamped' && (
                      <div className="border rounded-md overflow-hidden bg-muted/30" style={{ height: 280 }}>
                        {pdfLoading ? (
                          <div className="flex flex-col items-center justify-center h-full gap-2">
                            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">Cargando PDF…</p>
                          </div>
                        ) : pdfBlobUrl ? (
                          <div className="flex flex-col items-center justify-center h-full gap-3 p-4">
                            <Eye className="w-8 h-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground text-center">
                              Vista previa disponible — usa el botón <strong>PDF</strong> para descargar o ábrelo en nueva pestaña
                            </p>
                            <a
                              href={pdfBlobUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-primary underline font-medium"
                            >
                              Abrir PDF en nueva pestaña
                            </a>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full gap-2">
                            <Eye className="w-8 h-8 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">PDF no disponible</p>
                          </div>
                        )}
                      </div>
                    )}
                  </CardContent>
                </>
              ) : (
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <CheckCircle2 className="w-10 h-10 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground text-sm">Selecciona un CFDI de la lista para ver su detalle</p>
                </CardContent>
              )}
            </Card>
          </div>
        </TabsContent>

        {/* ── Complementos de Pago tab ────────────────────────────────────── */}
        <TabsContent value="complementos" className="space-y-4 mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left: facturas PPD con saldo pendiente */}
            <Card className="overflow-hidden">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-base">
                    Facturas PPD con saldo pendiente
                    {!ppdLoading && (
                      <Badge variant="secondary" className="ml-2">{filteredPpdInvoices.length}</Badge>
                    )}
                  </CardTitle>
                  <div className="relative w-56">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar alumno, folio…"
                      value={ppdSearch}
                      onChange={e => setPpdSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {ppdLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredPpdInvoices.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">
                    No hay facturas PPD con saldo pendiente
                  </p>
                ) : (
                  <div className="divide-y max-h-[520px] overflow-y-auto">
                    {filteredPpdInvoices.map(p => (
                      <div
                        key={p.id}
                        onClick={() => setSelectedPpdId(p.id)}
                        className={`px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors ${selectedPpdId === p.id ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{p.alumnoNombre || '—'}</p>
                            <p className="text-xs text-muted-foreground">
                              {p.folioFiscal || '—'} · {p.invoiceNumber || '—'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-0.5 shrink-0">
                            <span className="text-xs text-muted-foreground">Saldo</span>
                            <span className="text-sm font-bold">{formatAmount(p.saldoInsoluto)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right: pagos pendientes de complemento + acción */}
            <Card>
              {!selectedPpd ? (
                <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                  <CreditCard className="w-10 h-10 text-muted-foreground mb-3" />
                  <p className="text-muted-foreground text-sm">Selecciona una factura PPD de la lista</p>
                </CardContent>
              ) : (
                <>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">Detalle y receptor</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-2 text-sm rounded-md bg-muted/50 p-3">
                      <div>
                        <p className="text-xs text-muted-foreground">Folio Fiscal</p>
                        <p className="font-medium">{selectedPpd.folioFiscal || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Total factura</p>
                        <p className="font-medium">{formatAmount(selectedPpd.total)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Saldo insoluto</p>
                        <p className="font-bold">{formatAmount(selectedPpd.saldoInsoluto)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Alumno</p>
                        <p className="font-medium truncate">{selectedPpd.alumnoNombre || '—'}</p>
                      </div>
                    </div>

                    {/* Receptor — forzado a uso CFDI CP01 por el Hub. El SAT exige UsoCFDI=S01
                        para el RFC genérico XAXX010101000, así que "Público en General" no es
                        una opción válida para Complementos de Pago. */}
                    <div>
                      <Label className="text-xs text-muted-foreground">Receptor (uso CFDI CP01 — Pagos)</Label>
                      <Select
                        value={complementTaxProfileId?.toString() ?? undefined}
                        onValueChange={v => setComplementTaxProfileId(parseInt(v))}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Seleccionar receptor…" />
                        </SelectTrigger>
                        <SelectContent>
                          {complementTaxProfiles.map(p => (
                            <SelectItem key={p.id} value={p.id.toString()}>
                              <span className="font-medium">{p.rfc}</span>
                              {' — '}{p.profileName}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {complementTaxProfiles.length === 0 && (
                        <p className="text-xs text-amber-600 mt-1">
                          Este alumno no tiene un perfil fiscal registrado. Crea uno con su RFC real en
                          Perfiles Fiscales antes de generar el Complemento de Pago.
                        </p>
                      )}
                    </div>

                    {/* Pagos pendientes de complemento */}
                    <div>
                      <Label className="text-xs text-muted-foreground">
                        Pagos cobrados pendientes de Complemento de Pago
                      </Label>
                      <div className="mt-1 border rounded-md overflow-hidden">
                        {pendingPaymentsLoading ? (
                          <div className="flex justify-center py-6">
                            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                          </div>
                        ) : pendingComplementPayments.length === 0 ? (
                          <p className="text-center py-6 text-muted-foreground text-sm">
                            No hay pagos pendientes de complemento para esta factura
                          </p>
                        ) : (
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-10" />
                                <TableHead>Folio</TableHead>
                                <TableHead>Fecha</TableHead>
                                <TableHead>Método</TableHead>
                                <TableHead className="text-right">Monto</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pendingComplementPayments.map(p => (
                                <TableRow
                                  key={p.id}
                                  className={selectedComplementPaymentIds.has(p.id) ? 'bg-primary/5' : undefined}
                                  onClick={() => handleSelectComplementPayment(p.id, !selectedComplementPaymentIds.has(p.id))}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <TableCell onClick={e => e.stopPropagation()}>
                                    <Checkbox
                                      checked={selectedComplementPaymentIds.has(p.id)}
                                      onCheckedChange={checked => handleSelectComplementPayment(p.id, !!checked)}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm font-medium">{formatPaymentFolio(p) || '—'}</div>
                                    {p.reference && <div className="text-xs text-muted-foreground">{p.reference}</div>}
                                  </TableCell>
                                  <TableCell className="text-sm">{formatDate(p.date)}</TableCell>
                                  <TableCell>
                                    <Badge variant="outline" className="text-xs">{p.paymentMethod}</Badge>
                                  </TableCell>
                                  <TableCell className="text-right font-medium">{formatAmount(p.amount)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </div>
                    </div>

                    {complementError && (
                      <Alert variant="destructive" className="relative">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertTitle className="flex items-center gap-2">
                          Error al timbrar Complemento de Pago
                          {complementError.satCode && (
                            <code className="text-xs bg-destructive/20 px-1.5 py-0.5 rounded font-mono">
                              {complementError.satCode}
                            </code>
                          )}
                        </AlertTitle>
                        <AlertDescription className="mt-1 pr-8">
                          {complementError.message}
                          {complementError.satDetail && (
                            <div className="mt-1 text-xs opacity-80">{complementError.satDetail}</div>
                          )}
                        </AlertDescription>
                        <button
                          onClick={() => setComplementError(null)}
                          className="absolute top-3 right-3 opacity-70 hover:opacity-100 transition-opacity"
                          aria-label="Cerrar"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </Alert>
                    )}

                    {selectedComplementPaymentIds.size > 0 && (
                      <div className="flex items-center justify-between flex-wrap gap-3 rounded-md border border-primary/30 bg-primary/5 p-3">
                        <div>
                          <p className="text-sm font-medium">{selectedComplementPaymentIds.size} pago(s) seleccionado(s)</p>
                          <p className="text-lg font-bold">Total: {formatAmount(totalSelectedComplementAmount)}</p>
                        </div>
                        <Button
                          size="lg"
                          disabled={!canGenerateComplement}
                          onClick={() => { setComplementError(null); generateComplementMutation.mutate(); }}
                        >
                          {generateComplementMutation.isPending ? (
                            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Timbrando…</>
                          ) : (
                            <><CreditCard className="w-4 h-4 mr-2" />Generar Complemento de Pago</>
                          )}
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </>
              )}
            </Card>
          </div>

          {/* Complementos emitidos recientes — envío por correo */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Complementos de Pago emitidos recientemente</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {complementsLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : recentComplements.length === 0 ? (
                <p className="text-center py-6 text-muted-foreground text-sm">Aún no hay complementos emitidos</p>
              ) : (
                <div className="divide-y">
                  {recentComplements.map(c => (
                    <div key={c.id} className="flex items-center justify-between gap-3 px-4 py-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium truncate">{c.receptorName} <span className="text-muted-foreground font-normal">· {c.folioFiscal}</span></p>
                        <p className="text-xs text-muted-foreground">{formatDate(c.emittedAt)} · {formatAmount(c.total)}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(c)}>
                          <Download className="w-4 h-4 mr-1" />PDF
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => sendEmailMutation.mutate(c)}
                          disabled={sendEmailMutation.isPending}
                        >
                          {sendEmailMutation.isPending && sendEmailMutation.variables?.id === c.id
                            ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            : <Mail className="w-4 h-4 mr-1" />}
                          Enviar por correo
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* ── Cancel CFDI Dialog ─────────────────────────────────────────── */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancelar CFDI
            </DialogTitle>
            <DialogDescription>
              Esta acción enviará la solicitud de cancelación al SAT. El receptor tiene 72 horas para aceptar o rechazarla.
            </DialogDescription>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-muted/50 p-3 text-sm space-y-1">
                <p><span className="text-muted-foreground">Folio:</span> <span className="font-medium">{selectedInvoice.folioFiscal || '—'}</span></p>
                <p><span className="text-muted-foreground">UUID:</span> <span className="font-mono text-xs">{selectedInvoice.uuid || '—'}</span></p>
                <p><span className="text-muted-foreground">Total:</span> <span className="font-bold">{formatAmount(selectedInvoice.total)}</span></p>
              </div>

              <div className="space-y-2">
                <Label>Motivo de cancelación *</Label>
                <Select value={cancelMotivo} onValueChange={v => setCancelMotivo(v as '01' | '02' | '03' | '04')}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="01">01 — Comprobante emitido con errores con relación</SelectItem>
                    <SelectItem value="02">02 — Comprobante emitido con errores sin relación</SelectItem>
                    <SelectItem value="03">03 — No se llevó a cabo la operación</SelectItem>
                    <SelectItem value="04">04 — Operación nominativa relacionada en factura global</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {cancelMotivo === '01' && (
                <div className="space-y-2">
                  <Label>UUID del CFDI sustituto *</Label>
                  <Input
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    value={cancelUuidRelacionado}
                    onChange={e => setCancelUuidRelacionado(e.target.value)}
                    maxLength={36}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-muted-foreground">Requerido cuando el motivo es "con relación": indica el CFDI que sustituye a éste.</p>
                </div>
              )}
            </div>
          )}

          {cancelMutation.isError && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-700 flex items-start gap-2">
              <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{(cancelMutation.error as Error)?.message || 'Error al cancelar CFDI'}</span>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowCancelDialog(false); cancelMutation.reset(); }} disabled={cancelMutation.isPending}>
              Cerrar
            </Button>
            <Button
              variant="destructive"
              disabled={cancelMutation.isPending || (cancelMotivo === '01' && !cancelUuidRelacionado.trim())}
              onClick={() => {
                if (!selectedInvoice) return;
                cancelMutation.mutate({
                  id: selectedInvoice.id,
                  motivo: cancelMotivo,
                  uuidRelacionado: cancelMotivo === '01' ? cancelUuidRelacionado.trim() : undefined,
                });
              }}
            >
              {cancelMutation.isPending ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Cancelando…</>
              ) : (
                <><XCircle className="w-4 h-4 mr-2" />Confirmar cancelación</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
